import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Manages filter state with a draft/applied pattern.
 *
 * Draft values are local state — fast for inputs, no URL updates while typing.
 * Applied values come from URL search params — shareable, bookmarkable.
 *
 * Usage:
 *   const fb = useFilterBar({ search: '', stage: [], range: '30' });
 *
 *   <input value={fb.draft.search}
 *          onChange={e => fb.setDraft('search', e.target.value)}
 *          onKeyDown={fb.applyOnEnter} />
 *
 *   // Filter data based on applied (URL) state
 *   const filtered = data.filter(d => d.name.includes(fb.applied.search));
 *
 *   <FilterActions onApply={fb.apply} onClear={fb.clear}
 *                  isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
 *
 * @param {Object} defaults - { filterKey: defaultValue, ... }
 *   Supports strings, numbers, booleans, arrays (JSON-encoded), and null.
 * @returns {{ draft, applied, setDraft, apply, clear, isDirty, hasActiveFilters, applyOnEnter }}
 */
export function useFilterBar(defaults) {
  const defaultsRef = useRef(defaults);
  const keysRef = useRef(Object.keys(defaults));
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Compute applied state from URL (cached for referential stability) ---
  const rawKey = keysRef.current
    .map(k => { const v = searchParams.get(k); return v === null ? '\x00' : v; })
    .join('\x01');

  const appliedCacheRef = useRef({ key: null, value: null });
  let applied;

  if (appliedCacheRef.current.key === rawKey) {
    applied = appliedCacheRef.current.value;
  } else {
    applied = {};
    for (const [key, def] of Object.entries(defaultsRef.current)) {
      const raw = searchParams.get(key);
      if (raw === null) {
        applied[key] = def;
      } else if (Array.isArray(def)) {
        try { applied[key] = JSON.parse(raw); } catch { applied[key] = def; }
      } else if (typeof def === 'number') {
        const n = Number(raw);
        applied[key] = Number.isNaN(n) ? def : n;
      } else if (typeof def === 'boolean') {
        applied[key] = raw === 'true';
      } else {
        applied[key] = raw; // string or null default
      }
    }
    appliedCacheRef.current = { key: rawKey, value: applied };
  }

  // --- Draft state (local, for lag-free editing) ---
  const [draft, setDraftState] = useState(() => ({ ...applied }));
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Sync draft when URL changes externally (browser back / forward)
  const prevRawKeyRef = useRef(rawKey);
  useEffect(() => {
    if (prevRawKeyRef.current !== rawKey) {
      prevRawKeyRef.current = rawKey;
      setDraftState(prev => {
        // Only update if draft differs from applied (skip after our own apply)
        for (const key of keysRef.current) {
          const d = prev[key], a = applied[key];
          if (Array.isArray(a)) {
            if (JSON.stringify(d) !== JSON.stringify(a)) return { ...applied };
          } else if (d !== a) {
            return { ...applied };
          }
        }
        return prev; // Already matches — bail out
      });
    }
  }, [rawKey, applied]);

  // --- Actions ---
  const setDraft = useCallback((key, value) => {
    setDraftState(prev => ({ ...prev, [key]: value }));
  }, []);

  const apply = useCallback(() => {
    const d = draftRef.current;
    const defs = defaultsRef.current;

    setSearchParams(prev => {
      const next = new URLSearchParams(prev);

      for (const key of keysRef.current) {
        const value = d[key];
        const def = defs[key];

        let isDefault;
        if (Array.isArray(def)) {
          isDefault = !Array.isArray(value) || value.length === 0
            || JSON.stringify(value) === JSON.stringify(def);
        } else if (typeof def === 'boolean') {
          isDefault = value === def;
        } else {
          isDefault = value === def || value === '' || value == null;
        }

        if (isDefault) {
          next.delete(key);
        } else if (Array.isArray(value)) {
          next.set(key, JSON.stringify(value));
        } else {
          next.set(key, String(value));
        }
      }

      // Reset pagination when applying new filters
      next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clear = useCallback(() => {
    setDraftState({ ...defaultsRef.current });
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const key of keysRef.current) {
        next.delete(key);
      }
      next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /** Attach to search inputs: onKeyDown={fb.applyOnEnter} */
  const applyOnEnter = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      apply();
    }
  }, [apply]);

  // --- Computed ---
  const isDirty = useMemo(() => {
    return keysRef.current.some(key => {
      const d = draft[key], a = applied[key];
      if (Array.isArray(d) && Array.isArray(a)) {
        return JSON.stringify(d) !== JSON.stringify(a);
      }
      return d !== a;
    });
  }, [draft, applied]);

  const hasActiveFilters = useMemo(() => {
    const defs = defaultsRef.current;
    return keysRef.current.some(key => {
      const v = applied[key], d = defs[key];
      if (Array.isArray(d)) {
        return Array.isArray(v) && v.length > 0
          && JSON.stringify(v) !== JSON.stringify(d);
      }
      if (typeof d === 'boolean') return v !== d;
      return v !== d && v !== '' && v != null;
    });
  }, [applied]);

  return { draft, applied, setDraft, apply, clear, isDirty, hasActiveFilters, applyOnEnter };
}
